import type Dexie from 'dexie';

/**
 * Migration v001 — the initial harness schema.
 *
 * NFR-7: "Numbered forward-only Dexie migrations from v001." This is v001. Every later version
 * gets its own numbered file in this directory and is appended to `../migrations/index.ts`; no
 * file in here is ever edited after it has run on a device, because a device that already applied
 * v001 will never apply it again and an edit would silently produce two different v001 schemas in
 * the field.
 *
 * Design §13.3: **migrations never import `domain/`** — plain rows only — so a later refactor of
 * domain semantics cannot retroactively change what an old migration does. The only import here is
 * the Dexie type.
 *
 * ── Index choices ────────────────────────────────────────────────────────────
 * The two measured stores carry exactly the indexes design §3.4 specifies. This is the whole
 * reason the harness is worth running: index maintenance is a material part of an IndexedDB
 * commit, so measuring a bare object store would produce a fast, optimistic, useless number.
 *
 *   observations  [matchId+hypothesisId+kind+createdAt]  undo target selection (LIVE-6, INV-1)
 *                 [matchId+createdAt]                    replay, sync staleness, SES-8 re-derivation
 *   hypotheses    &[matchId+boardPosition]               INV-5 — compound entries with a null
 *                                                        component are omitted from an IndexedDB
 *                                                        index, so list-only rows are correctly
 *                                                        unconstrained. Do not "fix" this.
 *                 [matchId+statusOverride]               board render
 *                 [matchId+cachedCount]                  LOCK-2, DIST-2
 *
 * `logEntries` uses an auto-incrementing `seq` so the OBS-1 ring can be trimmed from the low end
 * without a scan.
 */
export function v001Harness(db: Dexie): void {
  db.version(1).stores({
    observations:
      'id, [matchId+hypothesisId+kind+createdAt], [matchId+createdAt], matchId, hypothesisId',
    hypotheses:
      'id, &[matchId+boardPosition], [matchId+statusOverride], [matchId+cachedCount], matchId',
    logEntries: '++seq, ts, level, module, matchId',
    bulkBlobs: 'id',
    runs: 'id, startedAt, condition',
  });
}
